import { useMemo, useState } from "react";
import { DateTime } from "luxon";
import { CalendarClock } from "lucide-react";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MonthGrid } from "@/components/ui/date-picker";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { addDays, customInstant, formatInstant, snoozePresets, toIsoUtc, todayIn } from "@/lib/dates";
import { useHierarchy } from "@/data/hooks";

/** Preset items with the resolved time visible before choosing (PRD §7). */
export function SnoozePresetItems({ onPick, onCustom }: { onPick: (iso: string) => void; onCustom: () => void }) {
  const { timezone } = useHierarchy();
  const presets = snoozePresets(timezone);
  return (
    <>
      {presets.map((p) => (
        <DropdownMenuItem key={p.key} onSelect={() => onPick(toIsoUtc(p.at))}>
          <span>{p.label}</span>
          <span className="ml-auto pl-6 text-[12px] text-subtle tabular">{formatInstant(p.at.toISO()!, timezone)}</span>
        </DropdownMenuItem>
      ))}
      <DropdownMenuItem onSelect={onCustom}>
        <CalendarClock />
        Custom…
      </DropdownMenuItem>
    </>
  );
}

/** Custom snooze: pick a day and time; the resolved instant is shown before confirming. */
export function CustomSnoozeDialog({
  open,
  onOpenChange,
  onConfirm,
  title = "Snooze until",
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onConfirm: (iso: string) => void;
  title?: string;
}) {
  const { timezone } = useHierarchy();
  const today = todayIn(timezone);
  const [date, setDate] = useState(addDays(today, 1));
  const [time, setTime] = useState("09:00");
  const at = useMemo(() => customInstant(date, time || "09:00", timezone), [date, time, timezone]);
  const valid = at.isValid && at > DateTime.now();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[320px]" onClick={(e) => e.stopPropagation()}>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <MonthGrid value={date} today={today} onSelect={setDate} min={today} />
          <div className="flex items-center gap-2">
            <label htmlFor="snooze-time" className="text-[13px] text-muted-foreground">
              Time
            </label>
            <Input id="snooze-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-[120px]" />
          </div>
          <p className="text-[13px] text-muted-foreground" aria-live="polite">
            {valid ? formatInstant(at.toISO()!, timezone) : "Pick a time in the future."}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="default"
            disabled={!valid}
            onClick={() => {
              onConfirm(toIsoUtc(at));
              onOpenChange(false);
            }}
          >
            Snooze
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
